import React from 'react';
import { TextInputProps } from 'grommet';
import { TChargingNetworkId } from './models/ChargingNetwork';
import { TChargingTypeId } from './models/ChargingType';
import { TEquipmentUseCaseId } from './models/EquipmentUseCase';
import { TForkliftClassificationId } from './models/ForkliftClassification';
import {
  Client,
  Equipment,
  Facility,
  Region,
  TAddressId,
  TClientId,
  TCountryId,
  TEquipmentCategoryId,
  TEquipmentTypeId,
  TFSERegistrationStatusId,
  TFacilityId,
  TFuelPathwayId,
  TProgramId,
  TRegionId,
  TRoleId,
  TUserId,
  TUtilityId,
  User,
} from '/src/lib/models';

/**
 * Aliases
 */
export type Integer = number;
export type IntegerString = string;

export type Float = number;
export type FloatString = string;

export type DateString = string;
export type QuarterString = string;

export type Coordinates = [lat: number, lng: number];

export type Nullable<T> = T | null;

export type TNodeEnv = 'development' | 'production' | 'test';

/**
 * Enums
 */
export enum Regions {
  // United States
  AL = 'AL',
  AK = 'AK',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DE = 'DE',
  FL = 'FL',
  GA = 'GA',
  HI = 'HI',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  ME = 'ME',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MO = 'MO',
  MT = 'MT',
  NE = 'NE',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY',
  // Canada
  AB = 'AB',
  BC = 'BC',
  MB = 'MB',
  NB = 'NB',
  NL = 'NL',
  NT = 'NT',
  NS = 'NS',
  NU = 'NU',
  ON = 'ON',
  PE = 'PE',
  QC = 'QC',
  SK = 'SK',
  YT = 'YT',
}

export enum CountryName {
  Canada = 'canada',
  USA = 'usa',
  Combined = 'combined',
}

export enum CountryId {
  USA = 1,
  Canada = 2,
}

export enum ReportingPeriodType {
  Quarterly = 1,
  Yearly = 2,
}

export enum StorageKeys {
  Theme = 'theme',
  CurrentDashboardId = 'currentDashboardId',
  SelectedDashboardData = 'selectedDashboardData',
  SelectedClient = 'selectedClient',
  SelectedClientID = 'selectedClientId',
  ActiveClientId = 'activeClientId',
  SelectedUser = 'selectedUser',
  SelectedUserID = 'selectedUserId',
  UserClients = 'userClients',
  UserClientsMeta = 'userClientsMeta',
  SessionToken = 'sessionToken',
  SessionUser = 'sessionUser',
  SessionExpiration = 'sessionExpiration',
  SessionTtl = 'sessionTtl',
  Session = 'session',
}

export enum LeafletOutputFormats {
  JSON = 'json',
  GeoJSON = 'geojson',
  GeocodeJSON = 'geocodejson',
}

export enum ModelType {
  Clients = 'Clients',
  Facilities = 'Facilities',
  Equipment = 'Equipment',
  Users = 'Users',
}

export enum EntityStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Retired = 'Retired',
}

export enum MeteredStatus {
  Metered = 'Metered',
  Unmetered = 'Unmetered',
}

export enum QuarterMonths {
  Q1 = '01',
  Q2 = '04',
  Q3 = '07',
  Q4 = '10',
}

export enum FinalizedStatus {
  NotFinalized = 0,
  Finalized = 1,
}

export enum LockedStatus {
  NotLocked = 0,
  Locked = 1,
}

export enum FSERegistrationStatusIds {
  'Pending Client Information' = 1,
  'Pending CARB Review' = 2,
  Approved = 3,
  'Not Approved' = 4,
  'Deduct from PoS' = 5,
  Paused = 6,
}

export type SearchableEntities = Client | Facility | Equipment | User;

/**
 * Form Fields
 */
export type SelectOption<T> = { label: string; value: T };

export type SelectOptions<T> = SelectOption<T>[];

/**
 * Info Form
 */
export enum InfoFormFieldType {
  Input = 'Input',
  Select = 'Select',
  SelectMultiple = 'SelectMultiple',
  Password = 'Password',
}

export type InfoFormField = {
  label: string;
  fieldType: InfoFormFieldType;
  value?: string | number;
  values?: (string | number)[];
  setValue?: (value: string | number) => void;
  setValues?: (values: (string | number)[]) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  inputType?: string;
  hide?: boolean;
  selectOptions?: Record<string, string | number>[];
  selectComponent?: React.ReactNode;
};

export type InfoFormFieldGroup = {
  fields: InfoFormField[];
  title?: string;
  hide?: boolean;
  columnCount?: number;
  emcAdminOnly?: boolean;
  showLineBefore?: boolean;
  showLineAfter?: boolean;
};

/**
 * Filters
 */
export type FilterItem = {
  label: string;
  value?: string | number;
  values?: string[] | number[];
  setValue?: (value: string | number) => void;
  setValues?: (values: Array<string | number>) => void;
  onClose?: () => void;
  onOpen?: () => void;
  onSearch?: (query: string) => void;
  type?: 'input' | 'select' | 'multi-select' | 'date';
  disabled?: boolean;
  isLoadingOptions?: boolean;
  emptySearchMessage?: string;
  options?: SelectOption<string | number | undefined>[] | string[];
  dateBounds?: [start: DateString, end: DateString];
};

/**
 * API
 */
export type TResponseMetadata = {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  first_page: number;
  first_page_url: string;
  last_page_url: string;
  next_page_url: string | null;
  previous_page_url: string | null;
};

export type TCredentialsType = 'include' | 'omit' | 'same-origin';

/**
 * Auth
 */
export type TSession = {
  token: TSessionToken;
  user: User;
};

export type TSessionToken = {
  type: string;
  token: string;
  expires_at: string;
};

/**
 * Libraries
 */
export type TNominatimOSMResponse = {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  class: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  address: {
    house_number: string;
    road: string;
    city: string;
    county: string;
    state: string;
    'ISO3166-2-lvl4': string;
    postcode: string;
    country: string;
    country_code: string;
  };
  boundingbox: string[];
};

/**
 * Charts & Graphs
 */
export type PieChartDataItem = { name: string; value: number; label?: string };
export type PieChartData = PieChartDataItem[];

export type BarChartDataItem = { name: string; barValue: string | number; lineValues?: (string | number)[] };
export type BarChartData = BarChartDataItem[];

/**
 * Revenue By Equipment Chart
 */
export type RevenueByEquipmentChartDatum = {
  name: string;
  value: string | number;
};

export type RevenueByEquipmentChartData = RevenueByEquipmentChartDatum[];

/**
 * Quarterly Incentive Revenue Chart
 */
export type QuarterlyIncentiveRevenueChartDatum = {
  quarter?: string;
  name: string;
  value: string | number;
  type?: 'bar' | 'line';
};
export type QuarterlyIncentiveRevenueChartData = QuarterlyIncentiveRevenueChartDatum[];

/**
 * Enrollment Trends Chart
 */
export type EnrollmentTrendsChartQuarterDatum = {
  quarter: string;
  value: number;
  num_facilities: number;
};

export type EnrollmentTrendsChartDatum = {
  category: string;
  quarters: EnrollmentTrendsChartQuarterDatum[];
};

export type EnrollmentTrendsChartData = EnrollmentTrendsChartDatum[];

/**
 * Equipment By Facility Chart
 */
export type EquipmentByFacilityChartFacilityDatum = {
  name: string;
  value: number;
};

export type EquipmentByFacilityChartDatum = {
  category: string;
  facilities: EquipmentByFacilityChartFacilityDatum[];
};

export type EquipmentByFacilityChartData = EquipmentByFacilityChartDatum[];

/**
 * Current Facility Locations Chart
 */
export type CurrentFacilityLocationsChartDatum = {
  id: number;
  name: string;
  address_1: string;
  address_2: string;
  coords?: Coordinates;
};

export type CurrentFacilityLocationsChartData = CurrentFacilityLocationsChartDatum[];

/**
 * Form Types
 */
export type FormFieldMetadata = {
  label: string;
  min?: number;
  max?: number;
  minValue?: number;
  maxValue?: number;
  minArrayLength?: number;
  required?: boolean;
  nonZero?: boolean;
  before?: DateString;
  sameOrBefore?: DateString;
  after?: DateString;
  sameOrAfter?: DateString;
};

export type FormMetadata = Record<string, FormFieldMetadata>;

export type FormErrors = Record<string, string>;

export type DataTableCellUnit = 'percent' | 'kwh';

export type DataTableCell = {
  required?: boolean;
  label: string;
  valueKey: string;
  isEditable?: boolean;
  unit?: DataTableCellUnit;
  inputMode?: TextInputProps['inputMode'];
  formatNumber?: boolean;
};

/**
 * Login Data Form
 */
export type LoginDataForm = {
  email: string;
  password: string;
};

/**
 * Program Commission Data Form
 */
export type ProgramCommissionDataForm = {
  program_id: TProgramId;
  is_percent_rate: boolean;
  rate: FloatString;
  [key: string]: TProgramId | boolean | FloatString;
};

/**
 * Sales Commission Data Form
 */
export type SalesCommissionDataForm = {
  user_id?: TUserId;
  percent_rate?: string;
};

export type ReferralCommissionDataForm = {
  referral_partner_id?: number;
  percent_rate?: string;
};

/**
 * Equipment Data Form
 */
export type EquipmentDataForm = {
  facility_id?: TFacilityId;
  category_name?: string;
  equipment_category_id?: TEquipmentCategoryId;
  equipment_type_id?: TEquipmentTypeId;
  unit_number?: Nullable<string>;
  serial_number: string;
  manufacturer: string;
  model_number: string;
  model_year: IntegerString;
  first_day_in_service?: Nullable<DateString>;
  first_active_reporting_quarter?: DateString;
  last_active_reporting_quarter?: Nullable<DateString>;
  is_metered: boolean;
  charging_network_id?: TChargingNetworkId;
  forklift_classification_id: Nullable<TForkliftClassificationId>;
  equipment_use_case_id: Nullable<TEquipmentUseCaseId>;
  charging_type_id: Nullable<TChargingTypeId>;
  nameplate_rating_kw: Nullable<number>;
  total_kwh?: number;
  latitude: FloatString;
  longitude: FloatString;
  fse_registration_status_id: TFSERegistrationStatusId;
  fse_id: Nullable<string>;
  fse_ru: Nullable<string>;
  comments: Nullable<string>;
};

/**
 * Unmetered Equipment Usage Data Form
 */
export type UnmeteredEquipmentUsageDataForm = {
  equipment_category_id?: TEquipmentCategoryId;
  fuel_pathway_id?: TFuelPathwayId;
  is_book_and_claim_applied: boolean | null;
  is_paused: boolean;
  battery_capacity_rating_ah: IntegerString;
  voltage: IntegerString;
  charge_cycles_per_shift: FloatString;
  shifts_per_day: FloatString;
  work_days_per_quarter: FloatString;
  percent_charger_efficiency_rating: IntegerString;
  percent_charge_return_factor: IntegerString;
  percent_depth_of_discharge: IntegerString;
  percent_uptime: Nullable<IntegerString>;
  max_site_deliverability_kw: Nullable<IntegerString>;
  [key: string]: TFuelPathwayId | boolean | string | undefined | null;
};

/**
 * Metered Equipment Usage Data Form
 */
export type MeteredEquipmentUsageDataForm = {
  equipment_category_id?: TEquipmentCategoryId;
  fuel_pathway_id?: TFuelPathwayId;
  is_book_and_claim_applied: boolean | null;
  total_kwh: Nullable<FloatString>;
  percent_uptime: Nullable<IntegerString>;
  max_site_deliverability_kw: Nullable<IntegerString>;
  [key: string]: TFuelPathwayId | boolean | string | undefined | null;
};

/**
 * Equipment Usage Combined Data Form
 */
export type EquipmentUsageDataForm = UnmeteredEquipmentUsageDataForm | MeteredEquipmentUsageDataForm;

/**
 * Facility Data Form
 */
export type FacilityDataFormValue =
  | string
  | Nullable<string>
  | Nullable<TRegionId>
  | Region
  | DateString
  | Nullable<DateString>
  | Nullable<TProgramId>
  | Nullable<TUtilityId>;

export type FacilityDataForm = {
  name: Nullable<string>;
  address_line1: string;
  address_line2: Nullable<string>;
  address_city: string;
  address_region_id: Nullable<TRegionId>;
  address_region?: Region;
  address_post_code: string;
  first_active_reporting_quarter?: DateString;
  last_active_reporting_quarter?: Nullable<DateString>;
  program_id: Nullable<TProgramId>;
  utility_id: Nullable<TUtilityId>;
  comments?: Nullable<string>;
};

/**
 * Create Client Form
 */
export type CreateClientForm = {
  name: string;
  website: string;
  business_id: string;
  start_date: string;
  end_date: Nullable<string>;
  first_active_reporting_quarter: DateString;
  jumpstart_amount: Nullable<string>;
  comments?: string;
  hq_address_line1: string;
  hq_address_line2: Nullable<string>;
  hq_address_city: string;
  hq_address_region_id: TRegionId;
  hq_address_post_code: string;
};

/**
 * Client Data Form
 */
export type ClientDataForm = {
  name: string;
  website: string;
  business_id: string;
  start_date: DateString;
  first_active_reporting_quarter: DateString;
  jumpstart_amount: Nullable<string>;
  comments?: Nullable<string>;
  hq_address_line1: string;
  hq_address_line2: Nullable<string>;
  hq_address_city: string;
  hq_address_region_id: TRegionId;
  country_id: TCountryId;
  hq_address_post_code: string;
  end_date?: DateString;
  hq_address_id?: TAddressId;
  programs: ProgramCommissionDataForm[];
  sales: SalesCommissionDataForm[];
  referral: ReferralCommissionDataForm[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

/**
 * User Data Form
 */
export type UserDataForm = {
  name: string;
  email: string;
  phone?: string;
  is_active?: boolean;
  is_email_verified: boolean;
  role_id: TRoleId;
  client_ids: TClientId[];
  facility_ids: TFacilityId[];
  add_facility_ids: TFacilityId[];
  remove_facility_ids: TFacilityId[];
};

/**
 * Model Audit Logs
 */
export enum ModelAuditLogEventType {
  Create = 'Create',
  Update = 'Update',
  Delete = 'Delete',
}

export enum ModelAuditLogState {
  Unresolved = 1,
  Resolved = 2,
}

export enum AuditorType {
  System = 'SYSTEM',
  InternalUser = 'INTERNAL_USER',
  ExternalUser = 'EXTERNAL_USER',
}

export type TModelAuditLogChangeDataItem = {
  field: string;
  old_value: Nullable<string | number>;
  new_value: Nullable<string | number>;
};

export type TModelAuditLogChangeData = TModelAuditLogChangeDataItem[];
